import { VoyageRoutePointFeature, VoyageRouteLineFeature, RouteGeometry, RoutePassagePlanProperties, WaypointUserInput, VoyageRoute, RoutePassagePlanUserInput } from '../../../src/models/voyage-routing-model';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { VoyagePlan, VesselCharacteristics, DraftsType } from '../../../src/models/vessel-planning-model';
import { getLegTypeFromDist } from '../../../src/utils/map-utils';
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils';
import { VesselEnpBookAndChapterModel } from '../../../src/models/vessel-enp-book-and-chapter-model';
import { VesselNavAreasModel } from '../../../src/models/vessel-nav-areas-model';
import { VesselRs1345Model } from '../../../src/models/vessel-rs1345-model';
import { VesselRs6Model } from '../../../src/models/vessel-rs6-model';
import { getLatestWeekDate, passagePlanPropertiesOnRoute } from '../../../src/utils/helpers';
import { GeometryUtils } from '../../../src/utils/GeometryUtils';
import { StringUtils } from '@azure/msal-browser';
import { VesselPerformanceModel } from '../../../src/models/vessel-performance-model';

export const MSPTOKNOTS = (60*60)/1852; // m/s -> knots

export interface WPAndMetadata
{
    waypoint: VoyageRoutePointFeature;
    coordinates: [number,number];
    metadata: {
        sequenceNumber: number;
        legType: string|null;
        course?: number|null;
        distance: {
            toNextWaypoint: number|null;
            remaining: number;
        };
    }
}

export interface CalculatedValues
{
    mpsToKnots: number;
    vesselMaxSpeed: number;
    totalDistance: number;
    routeMinSpeed: number|null;
    routeMaxSpeed: number|null;
    routeAverageSpeed: number|null;
    berthToPilot: number|null;
    pilotToPilot: number|null;
    pilotToBerth: number|null;
    timeBerthToPilot: number;
    speedBerthToPilot: number;
    timePilotToPilot: number;
    speedPilotToPilot: number;
    timePilotToBerth: number;
    speedPilotToBerth: number;
    timeTotal: number;
    speedTotal: number;
}

export interface wpMetadata {
    berthToPilot: WPAndMetadata[],
    pilotToPilot: WPAndMetadata[],
    pilotToBerth: WPAndMetadata[],
    berthToBerth: WPAndMetadata[]
}

export enum WPSections {
    BerthToPilot = "berthToPilot",
    PilotToPilot = "pilotToPilot",
    PilotToBerth = "pilotToBerth",
    BerthToBerth = "berthToBerth"
}

export class DataExtractor {

    static firstTs(waypoints: WPAndMetadata[]) {
        return Date.parse(waypoints.filter(waypoint => waypoint.waypoint.geometry.type === "Point" && waypoint.waypoint.properties.type === "Hard")[0].waypoint.properties.timestamp!);
    }

    static lastTs(waypoints: WPAndMetadata[]) {
        const points = waypoints.filter(waypoint => waypoint.waypoint.geometry.type === "Point" && waypoint.waypoint.properties.type === "Hard" && waypoint.waypoint.properties.timestamp);
        return Date.parse(points[points.length - 1].waypoint.properties.timestamp!);
    }

    static getSpeedAndTime(distance: number, startTime: number, endTime: number) {
        const time = (endTime - startTime) / (3600*1000);
        const speed = distance / time;

        return {time, speed };
    }

    static getCalculatedValues(plan: VoyagePlan, vesselCharacteristics: VesselCharacteristics, isOfflinePlan: boolean, showPassagePlanFromPlan: boolean|undefined): CalculatedValues
    {
        const currentSelectedRouteIndex = showPassagePlanFromPlan ? plan.selectedRouteIndex:  plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId);

        const route = plan.routes[currentSelectedRouteIndex];
        const waypoints = this.populateWaypoints(plan, vesselCharacteristics.ecdisWPStartIndex,showPassagePlanFromPlan);
        let timeBerthToPilot = 0;
        let speedBerthToPilot = 0;
        let timePilotToPilot = 0;
        let speedPilotToPilot = 0;
        let timePilotToBerth = 0;
        let speedPilotToBerth = 0;
        let distanceBerthToPilot = 0;
        let distancePilotToPilot = 0;
        let distancePilotToBerth = 0;
        
        if (waypoints.berthToPilot.length > 0) {

            for (let i = 0; i < waypoints.berthToPilot.length - 1; i++) {
                distanceBerthToPilot += waypoints.berthToPilot[i].metadata.distance.toNextWaypoint ?? 0;
            }
            distanceBerthToPilot = distanceBerthToPilot / 1.852    //convert to nautical miles from KM

            let firstTs = plan.etd;
            let lastTs = plan.departurePilotEtd;
            if (lastTs) {
                const result = this.getSpeedAndTime(distanceBerthToPilot, firstTs, lastTs);
                timeBerthToPilot = result.time;
                speedBerthToPilot = result.speed;
            }
        }

        if (waypoints.pilotToPilot.length > 0) {
            for (let i = 0; i < waypoints.pilotToPilot.length - 1; i++) {
                distancePilotToPilot += waypoints.pilotToPilot[i].metadata.distance.toNextWaypoint ?? 0;
            }
            distancePilotToPilot = distancePilotToPilot / 1.852    //convert to nautical miles from KM

            let firstTs = plan.departurePilotEtd;
            let lastTs = plan.arrivalPilotEta;
            if (firstTs && lastTs) {
                const result = this.getSpeedAndTime(distancePilotToPilot, firstTs, lastTs);
                timePilotToPilot = result.time;
                speedPilotToPilot = result.speed;
            }
        }

        if (waypoints.pilotToBerth.length > 0) {
            for (let i = 0; i < waypoints.pilotToBerth.length - 1; i++) {
                distancePilotToBerth += waypoints.pilotToBerth[i].metadata.distance.toNextWaypoint ?? 0;
            }
            distancePilotToBerth = distancePilotToBerth / 1.852    //convert to nautical miles from KM
            
            let firstTs = plan.arrivalPilotEta;
            let lastTs = plan.eta;
            if (lastTs) {
                const result = this.getSpeedAndTime(distancePilotToBerth, firstTs, lastTs);
                timePilotToBerth = result.time;
                speedPilotToBerth = result.speed;
            }
        }
        let totalDistance = distanceBerthToPilot + distancePilotToPilot + distancePilotToBerth; // in nautical miles

        const timeTotal: number|null = (Date.parse(new Date(plan!.eta).toISOString()) - Date.parse(new Date(plan!.etd).toISOString())) / (3600*1000);
        const speedTotal: number|null = totalDistance / timeTotal;
        
        return {
            mpsToKnots: (60*60)/1852, // m/s -> knots
            vesselMaxSpeed: vesselCharacteristics!.maxSpeed*((60*60)/1852),
            totalDistance,
            routeMinSpeed: plan.passagePlan.calculatedValues.routeMinSpeed,
            routeMaxSpeed: plan.passagePlan.calculatedValues.routeMaxSpeed,
            routeAverageSpeed: plan.passagePlan.calculatedValues.routeAverageSpeed,
            berthToPilot: distanceBerthToPilot,
            pilotToPilot: distancePilotToPilot,
            pilotToBerth: distancePilotToBerth,
            timeBerthToPilot,
            speedBerthToPilot,
            timePilotToPilot,
            speedPilotToPilot,
            timePilotToBerth,
            speedPilotToBerth,
            timeTotal,
            speedTotal
        }

        // TODO
        // Calculate squat (should be done by the routing API)
        // Get min depth from ENC data (ENC reader?)
        // Calculate UKC (under keel clearance) for berth to pilot, pilot to pilot and pilot to berth
    }

    // This function returns waypoints with metadata to be rendered in the UI
    //* TODO: This function is also changing properties in the original plan, for example setting the tide to 0 if it is not set.
    // This means other functionality is depending on this function running first. Values that are
    // required such as tide being 0 by default should be set earlier in the flow.
    static populateWaypoints(plan: VoyagePlan, WPStartIndex: number|null, isOfflinePlan?: boolean, showPassagePlanFromPlan?: boolean): wpMetadata
    {
        if (!plan)
        {
            return {
                berthToPilot: [],
                pilotToPilot: [],
                pilotToBerth: [],
                berthToBerth: []
            };
        }
        
        let wpStartIndex = WPStartIndex || 0;
        
        //const currentSelectedRouteIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId);
        const currentSelectedRouteIndex = showPassagePlanFromPlan ? plan.selectedRouteIndex:  plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId);

        const route = plan.routes[currentSelectedRouteIndex];
        let allHardWaypoints: WPAndMetadata[] = [];
        const legs: VoyageRouteLineFeature[] = [];

        // Create list of hard waypoints and legs
        route?.route_geometry?.forEach((feature, idx) => {
            if (feature.geometry.type === "Point" && feature.properties.type === "Hard")
            {
                const lastIdx = route?.route_geometry?.length - 1;
                const nextIdx = idx === lastIdx ? idx - 2 : idx + 1;

                let max_x_track = route?.route_geometry[nextIdx].properties.max_x_track || 0;
                max_x_track = Number(max_x_track.toFixed(2));

                // Add default values where needed
                // Add real turn rate from the ENC API if needed
                feature.properties = Object.assign({ tide: 0, turnrate: "1,0 * speed" }, feature.properties);
                feature.properties = {...feature.properties, max_x_track: max_x_track};

                allHardWaypoints.push(<WPAndMetadata>{
                    waypoint: feature,
                    coordinates: Array.from(feature.geometry.coordinates),
                    metadata: {}
                });
            }

            if (feature.geometry.type === "LineString")
            {
                legs.push(<VoyageRouteLineFeature>feature);
            }
        })
        
        allHardWaypoints = allHardWaypoints.filter(waypoint => !waypoint.waypoint.properties.is_stoppage_point || waypoint.waypoint.properties.stoppage_end === true);

        // Add the extra information we need to the waypoints
        let distanceSoFar = 0;
        let legIdx = -1;
        for (let i = 0; i < allHardWaypoints.length-1; i++)
        {
            const hardWaypoint = allHardWaypoints[i];
            const nextHardWPCoords = allHardWaypoints[i+1].coordinates;
            // Info points (e.g. current vessel position) causes the leg to be broken up into multiple legs. 
            // legsToNextHardWP is the list of legs from the current hard waypoint to the next hard waypoint
            const legsToNextHardWP: VoyageRouteLineFeature[] = [];
            
            do
            {
                if (legIdx >= legs.length) {
                    throw new Error("Leg index out of bounds");
                }
                legIdx++;
                legsToNextHardWP.push(legs[legIdx]);
            }
            while (legs[legIdx].geometry.coordinates[1][0] !== nextHardWPCoords[0] ||
                   legs[legIdx].geometry.coordinates[1][1] !== nextHardWPCoords[1])
            
            // Leg type is always taken from the starting hard point
            hardWaypoint.metadata.legType = legsToNextHardWP[0].properties.leg_type === "Rhumbline" ? "RL": "GC";
            hardWaypoint.metadata.sequenceNumber = i + wpStartIndex;
            // we will calculate the distance directly using the hard waypoints coordinates
                if (hardWaypoint.metadata.legType === "RL")
                {
                    hardWaypoint.metadata.course = CoordinatesUtils.calcCourse(hardWaypoint.coordinates, nextHardWPCoords);
                } else {
                    // If the leg type is GC, use WP course
                    hardWaypoint.metadata.course = hardWaypoint.waypoint.properties.course;
                }
                const dists = CoordinatesUtils.calcDistance(hardWaypoint.coordinates, nextHardWPCoords);
                hardWaypoint.metadata.distance = {
                    toNextWaypoint: (hardWaypoint.metadata.legType === "RL" ? dists.rl : dists.gc)*1.852,
                    remaining: distanceSoFar // subtracted from totalDistance later
                };

            distanceSoFar += hardWaypoint.metadata.distance.toNextWaypoint!;
        }

        // Last waypoint is a special case
        allHardWaypoints[allHardWaypoints.length-1].metadata = {
            sequenceNumber: allHardWaypoints.length + wpStartIndex - 1,
            legType: null,
            course: null,
            distance: {
                toNextWaypoint: null,
                remaining: 0
            }
        };
        
        // Update remaining distances now we have the total
        const totalDistance = distanceSoFar;
        for (let i = 0; i < allHardWaypoints.length-1; i++)
        {
            allHardWaypoints[i].metadata.distance.remaining = totalDistance - allHardWaypoints[i].metadata.distance.remaining;
        }

        let pilotOutIndex, pilotInIndex;
        ({ pilotOutIndex, pilotInIndex } = this._getPilotPointIndeces(allHardWaypoints, plan));

        const pilotToPilot = pilotOutIndex === pilotInIndex ? [] : allHardWaypoints.slice(pilotOutIndex === -1 ? 0 : pilotOutIndex, pilotInIndex === -1 ? allHardWaypoints.length : pilotInIndex + 1);

        return {
            berthToPilot: pilotOutIndex > -1 ? allHardWaypoints.slice(0, pilotOutIndex+1) : [],
            pilotToPilot,
            pilotToBerth: pilotInIndex > -1 ? allHardWaypoints.slice(pilotInIndex === -1 ? allHardWaypoints.length : pilotInIndex) : [],
            berthToBerth: pilotOutIndex === -1 && pilotInIndex === -1 ? allHardWaypoints : []
        };
    }

    static _getPilotPointIndeces(waypoints: WPAndMetadata[], plan: VoyagePlan)
    {
        let pilotOutIndex = -1;
        let pilotInIndex = -1;
        const pilotPointIndeces = [];

        // Find all pilot points
        for (let i = 0; i < waypoints.length; i++)
        {
            if (waypoints[i].waypoint.properties.pilotPoint)
            {
                pilotPointIndeces.push(i);
            }
        }

        // Pilot points - note that if we have only one pilot point it is both departure and arrival pilot point
        if (pilotPointIndeces.length >= 1)
        {
            pilotOutIndex = pilotPointIndeces[0];
            pilotInIndex = pilotPointIndeces[pilotPointIndeces.length-1];
        }

        return { pilotOutIndex, pilotInIndex };
    }

    static getAirDraft(plan: VoyagePlan, vesselCharacteristics: VesselCharacteristics, type: keyof DraftsType)
    {
        plan.passagePlan.drafts[type].airDraft = vesselCharacteristics.height - Math.min(plan.passagePlan.drafts[type].foreDraft || plan.foreDraft, plan.passagePlan.drafts[type].aftDraft || plan.aftDraft);  
        return  plan.passagePlan.drafts[type].airDraft?.toFixed(1);
    }

    static getResultingDraft(foreDraft: number, ppForeDraft: number|null, aftDraft: number, ppAftDraft: number|null): number {

        let resultingFore = foreDraft;
        if (ppForeDraft) {
            resultingFore = ppForeDraft;
        }
        let resultingAft = aftDraft;
        if (ppAftDraft) {
            resultingAft = ppAftDraft;
        }
    
        return Math.max(resultingFore, resultingAft);
    }

    static calcUKCs(plan: VoyagePlan, splitRoute: RouteGeometry[]) {
        for (const [sectionIdx, routeSection] of splitRoute.entries()) {
            var draft: number|null = null;
            switch (sectionIdx) {
                case 0:
                    draft = DataExtractor.getResultingDraft(plan.foreDraft, plan.passagePlan.drafts.berthToPilot.foreDraft,
                        plan.aftDraft, plan.passagePlan.drafts.berthToPilot.aftDraft)
                    break
                case 1:
                    draft = DataExtractor.getResultingDraft(plan.foreDraft, plan.passagePlan.drafts.pilotToPilot.foreDraft,
                        plan.aftDraft, plan.passagePlan.drafts.pilotToPilot.aftDraft)
                    break
                case 2:
                    draft = DataExtractor.getResultingDraft(plan.foreDraft, plan.passagePlan.drafts.pilotToBerth.foreDraft,
                        plan.aftDraft, plan.passagePlan.drafts.pilotToBerth.aftDraft)
                    break
            }
            if (!draft) {
                continue;
            }
    
            for (const feature of routeSection) {
                if (feature.properties.type !== "Hard") continue;
                const minDepthRaw = this.getUserOrSystemInputFromWP(<VoyageRoutePointFeature>feature, "min_depth");
                const catZocRaw = this.getUserOrSystemInputFromWP(<VoyageRoutePointFeature>feature, "catzoc");
                let tideRaw = this.getUserOrSystemInputFromWP(<VoyageRoutePointFeature>feature, "tide");
                // When tide is not set, it is by default 0.
                // Tide property is being set to 0 in 'populateWaypoints' function, which is 
                // probably not he right place to do it. The 'populateWaypoints' is editing the
                // waypoint properties, but it should only return the waypoints used for the passage plan UI
                if (!tideRaw) {
                    tideRaw = 0;
                }

                // CatZoc is only used for pilot-> pilot
                const ignoreCatZoc = sectionIdx !== 1;
                if (minDepthRaw && typeof tideRaw === "number" && (!ignoreCatZoc || catZocRaw)) {
                    const ukc = DataExtractor.calcUKC(minDepthRaw, tideRaw, draft, catZocRaw, ignoreCatZoc);
                    feature.properties.ukc = ukc;
                } else {
                    // Remove ukc property in case it was left over from a previous calculation
                    delete feature.properties.ukc;  
                }
            }
        }
        
        // Get and update the general UKC values
        plan.passagePlan.underKeelClearance.alongsideDeparture.minimumExpected = null;
        if (splitRoute[0].length > 0)  {
            const ukcDeparture = splitRoute[0][0].properties.ukc;
            
            if (ukcDeparture) {
                plan.passagePlan.underKeelClearance.alongsideDeparture.minimumExpected = ukcDeparture;
            }
        }
    
        plan.passagePlan.underKeelClearance.alongsideArrival.minimumExpected = null;
        if (splitRoute[2].length > 0) {
            const ukcArrival = splitRoute[2][(splitRoute[2].length)-1].properties.ukc;

            if (ukcArrival) {
                plan.passagePlan.underKeelClearance.alongsideArrival.minimumExpected = ukcArrival
            }
        }

        let minUkcConfined: number|null = null;;
        let minUkcOpen: number|null = null;

        for (const [sectionIdx, routeSection] of splitRoute.entries()) {
    
            let defaultOpenWaters = false;
            switch (sectionIdx) {
            case 0:
                defaultOpenWaters = false
                break
            case 1:
                defaultOpenWaters = true
                break
            case 2:
                defaultOpenWaters = false
                break
            }

            for (const [i, feature] of routeSection.entries()) {
                // Look at all hardpoints, except for the alongside waypoints (ie. the first and last waypoints)
                // The last waypoints in each split group is also the first in the next split group; so just always skip the last waypoint in a group
                if (feature.geometry.type == "Point" && feature.properties["type"] == "Hard" && i < routeSection.length-1 && !(sectionIdx == 0 && i == 0)) {
                    const ukc = feature.properties.ukc;
                    if (ukc) {
                        const openWaters = feature.properties.openWaters  !== undefined ? feature.properties.openWaters : defaultOpenWaters;

                        if (openWaters && (minUkcOpen === null || ukc < minUkcOpen)) {
                            minUkcOpen = ukc;
                        }
                        if (!openWaters && (minUkcConfined === null || ukc < minUkcConfined)) {
                            minUkcConfined = ukc
                        }
                    }
                }
            }
        }

        plan.passagePlan.underKeelClearance.confinedWater.minimumExpected = minUkcConfined;
        plan.passagePlan.underKeelClearance.openWater.minimumExpected = minUkcOpen;
    }


    static calcUKC(minDepth: number, tide: number, draft: number, catZoc: string, ignoreCatZoc: boolean): number {
        if (ignoreCatZoc) {
            return minDepth + tide - draft;
        }
    
        const catZocValue = DataExtractor.getCatZocUKCValue(catZoc, minDepth);
        if (!catZocValue) {
            return 0
        }
        
        return minDepth + tide - draft - catZocValue;
    }
    
    static getCatZocUKCValue(catZoc: string, minDepth: number): number|undefined {
        switch (catZoc) {
            case "A1":
                return 0.5 + 0.01*minDepth;
            case "A2":
            case "B":
                return 1.0 + 0.02*minDepth;
        
            case "C":
                return 2.0 + 0.05*minDepth;
        
            case "D":
            case "U":
                return (2.0 + 0.05*minDepth) * 1.1;
            default:
                return 0
            }
    }

    // Splits the route geometry into berth -> pilot point, pilot point -> pilot point and pilot point -> berth
    // Note: This function assumes that the legs/LineStrings are placed between its start and end waypoints in the list
    static splitRoute(plan: VoyagePlan): RouteGeometry[]|undefined {

        const currentSelectedRouteIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId);

        if (currentSelectedRouteIndex < 0 || currentSelectedRouteIndex >= plan.routes.length) {
            return;
        }

        const route = plan.routes[currentSelectedRouteIndex];
        let startIndex = -1;
        let endIndex = route?.route_geometry.length;

        // Find pilot points
        var pilotPointIndeces: number[] = [];

        for (const [index, feature] of route?.route_geometry.entries()) {
            if (feature.geometry.type == "Point") {
                if (feature.properties["pilotPoint"]) {
                    pilotPointIndeces.push(index);
                }
            }
        }

        // Note: One pilot point means it is both arrival and departure pilot point (ie. there is no pilot -> pilot)
        if (pilotPointIndeces.length >= 1) {
            startIndex = pilotPointIndeces[0];
            endIndex = pilotPointIndeces[pilotPointIndeces.length-1];
        }

        const splitRoute: [RouteGeometry, RouteGeometry, RouteGeometry]= [[],[],[]];
        for (const [index, feature] of route?.route_geometry.entries()) {
            if (index <= startIndex) {
                // Berth -> pilot
                splitRoute[0].push(feature);
            }
            if (index >= startIndex && index <= endIndex && startIndex != endIndex) {
                // Pilot -> pilot
                splitRoute[1].push(feature);
            }
            if (index >= endIndex) {
                // Pilot -> berth
                splitRoute[2].push(feature);
            }
        }
        return splitRoute;
    }

    static addCalculatedValues(plan: VoyagePlan, VesselCharacteristics: VesselCharacteristics|null) {
        // Populate plan with calculated values
        plan.passagePlan.calculatedValues = {
            avgSpeedOGPilotToPilot: null,
            berthToPilotDistance: null,
            pilotToBerthDistance: null,
            pilotToPilotDistance: null,
            routeAverageSpeed: null,
            routeMaxSpeed: null,
            routeMinSpeed: null,
        }
        
        plan.passagePlan.squats.minSpeed = {confinedWaters: null, openWaters: null}
        plan.passagePlan.squats.maxSpeed = {confinedWaters: null, openWaters: null}
        plan.passagePlan.squats.bufferSpeed = {confinedWaters: null, openWaters: null}
        plan.passagePlan.squats.vesselMaxSpeed = {confinedWaters: null, openWaters: null}

        const splitRoute = DataExtractor.splitRoute(plan);

        if (splitRoute) {
            const avgSpeed = DataExtractor.calcAvgSpeedOGPilotToPilot(plan, splitRoute[1])

            plan.passagePlan.calculatedValues.avgSpeedOGPilotToPilot = avgSpeed;
    
            const { berthToPilot, pilotToPilot, pilotToBerth } = DataExtractor.calcDistances(splitRoute);

            plan.passagePlan.calculatedValues.berthToPilotDistance = berthToPilot;
            plan.passagePlan.calculatedValues.pilotToPilotDistance = pilotToPilot;
            plan.passagePlan.calculatedValues.pilotToBerthDistance = pilotToBerth;
     
            DataExtractor.calcUKCs(plan, splitRoute)

            if (!VesselCharacteristics) {
                return;
            }
            
            // Calculate and add the general passage plan squats
            const vesselMaxSpeedKnots = VesselCharacteristics.maxSpeed * 60.0 * 60.0 / 1852
            const vesselMaxSpeedOpenSquat = DataExtractor.calcSquat(VesselCharacteristics.blockCoefficient, vesselMaxSpeedKnots, true)
            const vesselMaxSpeedConfinedSquat = DataExtractor.calcSquat(VesselCharacteristics.blockCoefficient, vesselMaxSpeedKnots, false)

            plan.passagePlan.squats.vesselMaxSpeed.openWaters = vesselMaxSpeedOpenSquat;
            plan.passagePlan.squats.vesselMaxSpeed.confinedWaters = vesselMaxSpeedConfinedSquat;
        }
    }

    static addOfflineCalculatedValues(plan: VoyagePlan, vesselCharacteristics: VesselCharacteristics | null): CalculatedValues | null {
        if (!vesselCharacteristics) return null;
    
        const mpsToKnots = MSPTOKNOTS;

        let calculatedValues: CalculatedValues | null = {
            mpsToKnots,
            vesselMaxSpeed: 0,
            totalDistance: 0,
            routeMinSpeed: null,
            routeMaxSpeed: null,
            routeAverageSpeed: null,
            berthToPilot: null,
            pilotToPilot: null,
            pilotToBerth: null,
            timeBerthToPilot: 0,
            speedBerthToPilot: 0,
            timePilotToPilot: 0,
            speedPilotToPilot: 0,
            timePilotToBerth: 0,
            speedPilotToBerth: 0,
            timeTotal: 0,
            speedTotal: 0
        }
       
        // Initialize squats
        const squats = plan.passagePlan.squats;
        squats.minSpeed = { confinedWaters: null, openWaters: null };
        squats.maxSpeed = { confinedWaters: null, openWaters: null };
        squats.bufferSpeed = { confinedWaters: null, openWaters: null };
        squats.vesselMaxSpeed = { confinedWaters: null, openWaters: null };
    
        const splitRoute = DataExtractor.splitRoute(plan);
        if (!splitRoute) return null;
    
        // Set route distances
        const { berthToPilot: berthToPilotDistance, pilotToPilot: pilotToPilotDistance, pilotToBerth: pilotToBerthDistance } = DataExtractor.calcDistances(splitRoute);
    
        // Perform UKC calculations
        DataExtractor.calcUKCs(plan, splitRoute);
    
        // Squat values
        const vesselMaxSpeedKnots = vesselCharacteristics.maxSpeed * 3600 / 1852;
        squats.vesselMaxSpeed.openWaters = DataExtractor.calcSquat(vesselCharacteristics.blockCoefficient, vesselMaxSpeedKnots, true);
        squats.vesselMaxSpeed.confinedWaters = DataExtractor.calcSquat(vesselCharacteristics.blockCoefficient, vesselMaxSpeedKnots, false);
    
        const routeAvgSpeed = calculatedValues.routeAverageSpeed;
        const totalDistance = (berthToPilotDistance ?? 0) + (pilotToPilotDistance ?? 0) + (pilotToBerthDistance ?? 0);
        const etdTime = new Date(plan.etd).getTime();
        const etaTime = new Date(plan.eta).getTime();
        const timeTotal = (etaTime - etdTime) / 3600000;
        const speedTotal = totalDistance / (timeTotal || 1); // avoid division by 0

        const departurePilotEtd = plan.departurePilotEtd ? new Date(plan.departurePilotEtd).getTime() : 0;
        const arrivalPilotEta = plan.arrivalPilotEta ? new Date(plan.arrivalPilotEta).getTime() : 0;

        let timeBerthToPilot = 0;
        let speedBerthToPilot = 0;
        let timePilotToPilot = 0;
        let speedPilotToPilot = 0;
        let timePilotToBerth = 0;
        let speedPilotToBerth = 0;

        [timeBerthToPilot, speedBerthToPilot] = DataExtractor.calcTimeAndSpeed(berthToPilotDistance, etdTime, departurePilotEtd);
        [timePilotToPilot, speedPilotToPilot] = DataExtractor.calcTimeAndSpeed(pilotToPilotDistance, departurePilotEtd, arrivalPilotEta);
        [timePilotToBerth, speedPilotToBerth] = DataExtractor.calcTimeAndSpeed(pilotToBerthDistance, arrivalPilotEta, etaTime);
    
        return {
            mpsToKnots,
            vesselMaxSpeed: vesselCharacteristics.maxSpeed * mpsToKnots,
            totalDistance,
            routeMinSpeed: calculatedValues.routeMinSpeed,
            routeMaxSpeed: calculatedValues.routeMaxSpeed,
            routeAverageSpeed: routeAvgSpeed,
            berthToPilot: berthToPilotDistance ?? 0,
            pilotToPilot: pilotToPilotDistance ?? 0,
            pilotToBerth: pilotToBerthDistance ?? 0,
            timeBerthToPilot,
            speedBerthToPilot,
            timePilotToPilot,
            speedPilotToPilot,
            timePilotToBerth,
            speedPilotToBerth,
            timeTotal,
            speedTotal
        };
    }

    static calcSquat(blockCoefficient: number, stwKnots: number, openWaters: boolean): number {
        // Calculate squat using simplified version of Dr. Barass' formula
        if (openWaters) {
            return blockCoefficient * stwKnots * stwKnots / 100.0
        } else {
            return blockCoefficient * stwKnots * stwKnots / 50.0
        }
    }

    static calcDistances(splitRoute: RouteGeometry[]): {berthToPilot: number|null, pilotToPilot: number|null, pilotToBerth: number|null} {
        const distances = [];
    
        for (const [index, features] of splitRoute.entries()) {
            if (features.length > 0) {
                let sum = DataExtractor.sumDistance(features);
                
                sum = sum / 1.852 // Convert to nm
                distances[index] = sum;
            } else {
                distances[index] = null;
            }
        }
    
        return {berthToPilot: distances[0], pilotToPilot: distances[1], pilotToBerth: distances[2]};
    }

    static calcAvgSpeedOGPilotToPilot(plan: VoyagePlan, pilotToPilotRoute: RouteGeometry): number {
        // Calculate average speed over ground for pilot -> pilot
        const distanceInKm = DataExtractor.sumDistance(pilotToPilotRoute)
        
        if (distanceInKm === 0) {
            const avgSpeed = 0.0;
            return avgSpeed;
        }
    
        // First point may not have a timestamp property (if it is the first waypoint of the route)
        // Use plan.Etd as fallback in that case
        let startTime = plan.etd;
        let timestring: string = pilotToPilotRoute[0].properties.eta;
        if (timestring) {
            startTime  = new Date(timestring).getTime();
        }
    
        timestring = pilotToPilotRoute[pilotToPilotRoute.length-1].properties.eta;
        const endTime = new Date(timestring).getTime();
        const avgSpeed = (distanceInKm / 1.852) / ((endTime-startTime)/(1000*60*60));

        return avgSpeed;
    }

    static sumDistance(features: RouteGeometry): number {
        let distanceInKm = 0;
        let prevHardPointCoords: number[] = [];
        let lineString: VoyageRouteLineFeature|null = null;
        for (const feature of features) {
            if (feature.geometry.type == "Point") {
                const typeProp = feature.properties.type;
    
                if (typeProp === "Hard") {
                    const coords = feature.geometry.coordinates;
    
                    if (prevHardPointCoords && lineString) {
                        const lineCoords = lineString.geometry.coordinates
                        if (prevHardPointCoords[0] == lineCoords[0][0] && prevHardPointCoords[1] == lineCoords[0][1]) {
                            const distance = lineString.properties.distance;
                            if (!distance) {
                                throw console.error("Missing or wrong type for distance property of leg %v", lineString);
                            }
                            distanceInKm += distance;
                        } else {
                            // Navigational points in between, calculate the direct distance
                            distanceInKm += DataExtractor.getPointToPointDist(lineString.properties.leg_type!, prevHardPointCoords, coords) / 1000
                        }
                    }
    
                    prevHardPointCoords = coords
                }
            } else if (feature.geometry.type == "LineString") {
                lineString = <VoyageRouteLineFeature>feature;
            }
        }
    
        return distanceInKm;
    }

    static getPointToPointDist(legType: "GreatCircle"|"Rhumbline", p1: number[], p2: number[]): number {
        if (legType === "GreatCircle") {
            return GeometryUtils.getHaversineDist(p1, p2);
        } else {
            return GeometryUtils.getRhumblineDist(p1, p2);
        }
    }

    static getNumber(value: number|null)
    {
        return Number.isFinite(value!) ? value : "";
    }

    static getDataFromPlan(propName: string, plan: VoyagePlan|null, showPassagePlanFromPlan?:boolean): string|number
    {
        if (!plan)
        {
            return "";
        }
        const currentSelectedRouteIndex = showPassagePlanFromPlan ? plan.selectedRouteIndex:  plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId);

        if (passagePlanPropertiesOnRoute.includes(propName) 
                && (!plan.routes || !plan.routes[currentSelectedRouteIndex] || !plan.routes[currentSelectedRouteIndex].passagePlan)) {
            return "";
        }

        if (propName === "avgSpeedOverGround")
        {
            if (!plan.passagePlan.calculatedValues?.avgSpeedOGPilotToPilot) {
                return "";
            }
            // Always auto-populated
            return plan.passagePlan.calculatedValues.avgSpeedOGPilotToPilot!.toFixed(1) + " kt";
        }

        if (propName === "paperCharts" && !plan.routes[currentSelectedRouteIndex].passagePlan!.notEcdisCompliant)
        {
            // Not used for ECDIS compliant passage plans
            return "N/A";
        }

        if (propName === "safetyMessagesNavareaRegions") {
            if (!plan.routes[currentSelectedRouteIndex]?.passagePlan!.safetyMessages.navareaRegions) {
                if (plan.routes[currentSelectedRouteIndex] && plan.routes[currentSelectedRouteIndex]?.passagePlan && VesselNavAreasModel) {
                    return VesselNavAreasModel!.getCrossedNavAreas(plan.routes[currentSelectedRouteIndex]?.route_geometry);
                }
            }

            return plan.routes[currentSelectedRouteIndex]?.passagePlan!.safetyMessages.navareaRegions;
        }

        if (propName === "weatherInfoNavareaRegions") {
            if (!plan.routes[currentSelectedRouteIndex]?.passagePlan!.weatherInfo.navareaRegions) {
                if (plan.routes[currentSelectedRouteIndex] && VesselNavAreasModel) {
                    return VesselNavAreasModel!.getCrossedNavAreas(plan.routes[currentSelectedRouteIndex]?.route_geometry);
                }
            }

            return plan.routes[currentSelectedRouteIndex]?.passagePlan!.weatherInfo.navareaRegions;
        }

        if (propName === "weatherInfoNavtexStations") {
            if (!plan.routes[currentSelectedRouteIndex]?.passagePlan!.weatherInfo.navtexStations) {
                if (plan.routes[currentSelectedRouteIndex] && VesselRs1345Model) {
                    return VesselRs1345Model!.getWeatherStationsCrossingRoute(plan.routes[currentSelectedRouteIndex]?.route_geometry);
                }
            }

            return plan.routes[currentSelectedRouteIndex]?.passagePlan!.weatherInfo.navtexStations;
        }

        if (propName === "safetyMessagesNavtexStations") {
            if (!plan.routes[currentSelectedRouteIndex]?.passagePlan!.safetyMessages.navtexStations) {
                if (plan.routes[currentSelectedRouteIndex] && VesselRs1345Model) {
                    return VesselRs1345Model!.getNavWarningStationsCrossingRoute(plan.routes[currentSelectedRouteIndex]?.route_geometry);
                }
            }

            return plan.routes[currentSelectedRouteIndex]?.passagePlan!.safetyMessages.navtexStations;
        }

        if (propName === "pubCorrectedToWeek") {
            if (!plan.routes[currentSelectedRouteIndex]?.passagePlan!.pubCorrectedToWeek) {
                if (VesselRs1345Model && VesselRs6Model) {
                    const latestRs1345 = VesselRs1345Model.latestUpdateDate;
                    const latestRs6 = VesselRs6Model.latestUpdateDate;

                    if (latestRs1345 && latestRs6) {
                        const latestDate = getLatestWeekDate([latestRs6, latestRs1345])!.split('/');
                        return `Week ${latestDate[0]}, ${latestDate[1]}`; // in ex. Week 23, 2021
                    }
                } 
            }

            return plan.routes[currentSelectedRouteIndex]?.passagePlan!.pubCorrectedToWeek;
        }

        if (propName === "planDate") {
            const planDate = plan.routes[currentSelectedRouteIndex]?.passagePlan?.planDate;
        
            if (!planDate) {
                return new Date().toISOString();
            }
        
            try {
                return this.formatDate(planDate);
            } catch (error) {
                console.error("Error formatting date in passage plan: ", error);
            }
        }

        let value:string|number = "";
        if (passagePlanPropertiesOnRoute.includes(propName)) {
            value = (<string|number>plan.routes[currentSelectedRouteIndex]!.passagePlan![<keyof RoutePassagePlanProperties>propName]);
        } else {
            value = <string|number>plan.passagePlan[<keyof VoyagePlan["passagePlan"]>propName];
        }
        
        if (!value)
        {
            // Some values are auto-populated - handle here
            switch (propName)
            {
                case "description":
                    value = this._getDescriptionFromTerminals(plan);
                    break;

                case "departurePort":
                    value = this._getPortNameFromTerminal(plan.departureTerminalCode);
                    break;

                case "arrivalPort":
                    value = this._getPortNameFromTerminal(plan.arrivalTerminalCode);
                    break;

                case "portMemo":
                    value = plan.arrivalTerminalCode ? TerminalsModel!.getCity(plan.arrivalTerminalCode)! : "";
                    break;

                case "encCellsUsed":
                    if (plan.passagePlan.notEcdisCompliant)
                    {
                        value = "Up to date charts of appropriate scale used for part of the voyage";
                    }
                    else
                    {
                        value = "Up to date charts of appropriate scale used for the voyage";
                    }
                    break;
                case "sailingDirections":
                    if (plan.routes[currentSelectedRouteIndex] && VesselEnpBookAndChapterModel) {
                        value = VesselEnpBookAndChapterModel!.getCrossedEnpChaptersAndBooks(plan.routes[currentSelectedRouteIndex]?.route_geometry);
                    }
                    break;
                case "sailingDirections":
                    if (plan.routes[currentSelectedRouteIndex] && VesselEnpBookAndChapterModel) {
                        value = VesselEnpBookAndChapterModel!.getCrossedEnpChaptersAndBooks(plan.routes[currentSelectedRouteIndex]?.route_geometry);
                    }
                    break;
            }
        }
        return value ? value : "";
    }

    static convertToNumberAndRound(value: string|number, decimals: number)
    {
        let num = Number(value);
        if (!Number.isFinite(num))
        {
            return "";
        }

        return num.toFixed(decimals);
    }

    static formatDate(inputDate: string): string {
        const parsedDate = new Date(inputDate);
        
        if (isNaN(parsedDate.getTime())) {
          throw new Error("Invalid date format");
        }
        
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
    }

    static getUserOrSystemInputFromWP(waypoint: VoyageRoutePointFeature, prop: keyof WaypointUserInput): any {
        // At the time of implementing 'userInput' prop, current plans might not contain this change.
        //     So, we need to check if it exists and if not, create it.
        if (!waypoint.properties.userInput) {
            waypoint.properties.userInput = {};
        }
        
        // Check if userInput exists
        if (waypoint.properties.userInput[prop] !== undefined) {
            return waypoint.properties.userInput[prop];
        }

        return waypoint.properties[prop];
        
    }

    static setUserInputOnWP(waypoint: VoyageRoutePointFeature, prop: keyof WaypointUserInput, value: any) {
        // Check if userInput exists, if not, create it as an empty object
        if (!waypoint.properties.userInput) {
            waypoint.properties.userInput = {};
        }

        waypoint.properties.userInput[prop] = value;
    }
    
    static getUserOrSystemInputFromRoutePassagePlan(route: VoyageRoute, prop: keyof RoutePassagePlanUserInput): {type:"system"|"user"|undefined, value: any} {
        // At the time of implementing 'userInput' prop, current plans might not contain this change.
        //     So, we need to check if it exists and if not, create it.
        if (!route.passagePlan!.userInput) {
            route.passagePlan!.userInput = {};
        }
        // Check if userInput exists
        if (route.passagePlan!.userInput[prop] !== undefined) {
            return {type: "user", value: route.passagePlan!.userInput[prop]};
        }

        switch (prop) {
            case "weatherInfoNavtexStations":
                return {type: "system", value: route.passagePlan?.weatherInfo.navtexStations};
            case "weatherInfoNavareaRegions":
                return {type: "system", value: route.passagePlan?.weatherInfo.navareaRegions};
            case "safetyMessagesNavtexStations":
                return {type: "system", value: route.passagePlan?.safetyMessages?.navtexStations};
            case "safetyMessagesNavareaRegions":
                return {type: "system", value: route.passagePlan?.safetyMessages?.navareaRegions};
            case "sailingDirections":
                return {type: "system", value: route.passagePlan?.sailingDirections};
            default:
                return {type: undefined, value: undefined};
        }
    }

    static setUserInputOnRoutePassagePlan(route: VoyageRoute, prop: keyof RoutePassagePlanUserInput, value: any) {
        // Check if userInput exists, if not, create it as an empty object
        if (!route.passagePlan!.userInput) {
            route.passagePlan!.userInput = {};
        }

        route.passagePlan!.userInput[prop] = value;
    }

    static getRequiredUKC(propName: keyof VoyagePlan["passagePlan"]["underKeelClearance"], plan: VoyagePlan|null): string|null
    {
        if (!plan)
        {
            return "";
        }

        let value = null;
        switch (propName)
        {
            case "alongsideDeparture":
                value = "0.30 m or 1 degree list whichever is greater: " + DataExtractor.convertToNumberAndRound(plan.passagePlan.underKeelClearance[propName].required, 2) + " m";
                break;
            
            case "alongsideArrival":
                value = "0.30 m or 1 degree list whichever is greater: " + DataExtractor.convertToNumberAndRound(plan.passagePlan.underKeelClearance[propName].required, 2) + " m";
                break;

            case "confinedWater":
                value = "0.90 m or 3 degree list whichever is greater: " + DataExtractor.convertToNumberAndRound(plan.passagePlan.underKeelClearance[propName].required, 2) + " m";
                break;

            case "openWater":
                value = "50% of vessels draught or 3.5 m whichever is greater: " + DataExtractor.convertToNumberAndRound(plan.passagePlan.underKeelClearance[propName].required, 2) + " m";
                break;
        }
        return value;
    }

    static _getDescriptionFromTerminals(plan: VoyagePlan)
    {
        let departureName = "At-Sea";
        let arrivalName = "At-Sea";

        if (plan.departureTerminalCode)
        {
            if (TerminalsModel!.isPseudoTerminal(plan.departureTerminalCode))
            {
                departureName = plan.departureTerminalCode;
            }
            else
            {
                departureName = TerminalsModel!.getCity(plan.departureTerminalCode) || plan.departureTerminalCode;
            }
        }

        if (plan.arrivalTerminalCode)
        {
            if (TerminalsModel!.isPseudoTerminal(plan.arrivalTerminalCode))
            {
                arrivalName = plan.arrivalTerminalCode;
            }
            else
            {
                arrivalName = TerminalsModel!.getCity(plan.arrivalTerminalCode) || plan.arrivalTerminalCode;
            }
        }

        return `${departureName} - ${arrivalName}`;
    }

    static _getPortNameFromTerminal(terminalId: string)
    {
        let value = "";
        if (terminalId && !TerminalsModel!.isPseudoTerminal(terminalId))
        {
            const termInfo = TerminalsModel!.getTerminal(terminalId);
            if (termInfo)
            {
                value = `${termInfo.city} - ${termInfo.location} (${terminalId})`;
            }
        }
        return value;
    }

    static calcTimeAndSpeed(distance: number | null, startTs: number, endTs: number): [number, number] {
        if (!distance || !startTs || !endTs) return [0, 0];
        const time = (endTs - startTs) / 3600000; // ms to hours
        if (time <= 0) return [0, 0];
        const speed = distance / time;
        return [time, speed];
    }
    
    static async _calculateRateOfTurnForWaypoint(waypoint: any): Promise<number | null> {
        const turnRadius = this.getUserOrSystemInputFromWP(waypoint.waypoint, "turn_radius"); // in nm
        if (!turnRadius) return null;

        const vesselCharacteristics = await VesselPerformanceModel!.getConfigurationData();
        const speedMax = this.getUserOrSystemInputFromWP(waypoint.waypoint, "speed_maximum");

        // Fallback to vessel max speed if speed at waypoint is not defined
        // props.speed_maximum = m/s, vesselCharacteristics.maxSpeed = knots
        const speedKnots = speedMax ? speedMax * 1.94384 : vesselCharacteristics?.maxSpeed ?? null;

        if (!speedKnots) return null;
        const turnRate = Math.round((speedKnots / turnRadius));
        this.setUserInputOnWP(waypoint.waypoint, "rate_of_turn", turnRate);
        return turnRate;
    }

    static computeTurnRadius(waypoint: VoyageRoutePointFeature, prop: keyof WaypointUserInput, waypoints: WPAndMetadata[], 
        waypointsCombined: {
            berthToPilot: WPAndMetadata[], 
            pilotToPilot: WPAndMetadata[], 
            pilotToBerth: WPAndMetadata[]
        }, index: number): any {
        const waypointsCombinedRaw = waypointsCombined.berthToPilot.concat(waypointsCombined.pilotToPilot).concat(waypointsCombined.pilotToBerth);
        // Compute Course Change (CC)
        // CC = min((DC - AC) mod 360, (AC - DC) mod 360)
        // Always in range [0, 180]
        const prevIndex = index - 1;
        let previousWaypoint = waypoints[prevIndex];
        if(!previousWaypoint) {
            const matchCurrentWPIndex = waypointsCombinedRaw.findIndex((wp) =>
                wp.waypoint === waypoint
            );
            previousWaypoint = waypointsCombinedRaw[matchCurrentWPIndex-1];
        }
        if (!previousWaypoint) return null;
        const arrivalCourse = previousWaypoint?.metadata.course;
        const currentWaypoint = waypoints[index];
        const departureCourse = currentWaypoint?.metadata.course;

        if (arrivalCourse == null || departureCourse == null) return null;

        const courseChange = Math.min(
            ((departureCourse - arrivalCourse) + 360) % 360,
            ((arrivalCourse - departureCourse) + 360) % 360
        );    

        // Compute Max Cross Track (MCT)
        // MCT = min(ACT, DCT)
        // Use getUserOrSystemInputFromWP to respect user overrides to max_x_track
        const arrivalCrossTrackRaw = this.getUserOrSystemInputFromWP(previousWaypoint.waypoint, "max_x_track");
        const arrivalCrossTrack = arrivalCrossTrackRaw != null ? Number(arrivalCrossTrackRaw) : undefined;
        const departingCrossTrackRaw = this.getUserOrSystemInputFromWP(waypoint, "max_x_track");
        const departingCrossTrack = departingCrossTrackRaw != null ? Number(departingCrossTrackRaw) : undefined;
        const maxCrossTrack = Math.min(arrivalCrossTrack ?? Infinity, departingCrossTrack ?? Infinity);

        // Calculate Computed Turn Radius (CTR)
        // CTR = MCT / (1 - COS(RADIANS(CC / 2)))
        // Guard against CC = 0 (straight course, denominator = 0)
        const denominator = 1 - Math.cos(courseChange * Math.PI / 360);
        if (denominator === 0) return null;
        const computedTurnRadius = maxCrossTrack / denominator;

        // Apply User Max Turn Radius Constraint
        // TR = min(UTR, CTR)
        // User-defined Max Turn Radius (UTR) we have a default max turn radius set to 2 nm 
        // ( have plans for future development where user can define this in the vessel characteristic)
        const turnRadius = Math.round(Math.min(2, computedTurnRadius) * 100) / 100;

        // Set the calculated turn radius as user input on the waypoint
        this.setUserInputOnWP(waypoint, "turn_radius", turnRadius);

        return turnRadius;
        
    }

    static async computeRateOfTurn(waypoint: VoyageRoutePointFeature, prop: keyof WaypointUserInput,  waypoints: WPAndMetadata[],
        waypointsCombined: {
            berthToPilot: WPAndMetadata[], 
            pilotToPilot: WPAndMetadata[], 
            pilotToBerth: WPAndMetadata[]
        },
         index: number): Promise<any> {
        // Respect user-entered turn radius first; compute one only when missing.
        let turnRadius = this.getUserOrSystemInputFromWP(waypoint, "turn_radius");
        if (turnRadius == null) {
            turnRadius = await this.computeTurnRadius(waypoint, prop, waypoints, waypointsCombined, index);
        }
        // Determine Speed for ROT
        const speedForROT = await this.computeSpeed(waypoint);

        // Calculate Rate of Turn (ROT)
        // ROT = (3 * Speed) / (PI * TR)
        // Guard against null TR (first WP, or CC = 0) and TR = 0 (division by zero)
        if (!turnRadius || speedForROT == null) {
            this.setUserInputOnWP(waypoint, "rate_of_turn", null);
            return null;
        }
        const rateOfTurn = Math.round((3 * speedForROT) / (Math.PI * Number(turnRadius)) * 10) / 10;
        // Set the calculated turn rate as user input on the waypoint
        this.setUserInputOnWP(waypoint, "rate_of_turn", rateOfTurn);

        return rateOfTurn;
    }


    static async computeSpeed(waypoint: VoyageRoutePointFeature): Promise<number | null> {
        // departingLegMaxSpeed (LMS) — stored in m/s by the routing API
        // vesselMaxSpeed (VMS)       — stored in knots by VesselPerformanceModel
        // Speed returned is always in knots for consistent use in the ROT formula
        const departingLegMaxSpeed = waypoint.properties.speed_maximum;
        if (departingLegMaxSpeed) {
            return departingLegMaxSpeed * MSPTOKNOTS; // convert m/s → knots
        }
        const configData = await VesselPerformanceModel?.getConfigurationData();
        return configData?.maxSpeed ?? null; // already in knots
    }

    static async updateMaxXTrack(waypoint: VoyageRoutePointFeature, prop: keyof WaypointUserInput, waypoints: WPAndMetadata[], 
        waypointsCombined: {
            berthToPilot: WPAndMetadata[], 
            pilotToPilot: WPAndMetadata[], 
            pilotToBerth: WPAndMetadata[]
        },index: number, value: any): Promise<any> {
        // Check if userInput exists, if not, create it as an empty object
        if (!waypoint.properties.userInput) {
            waypoint.properties.userInput = {};
        }

        waypoint.properties.userInput[prop] = value;

        await this.computeTurnRadius(waypoint, prop, waypoints, waypointsCombined, index);
        await this.computeRateOfTurn(waypoint, prop, waypoints, waypointsCombined, index);
        
        // Check if userInput exists
        if (waypoint.properties.userInput[prop] !== undefined) {
            return waypoint.properties.userInput[prop];
        }

        return waypoint.properties[prop];
    }
}
